import BigNumber from "bignumber.js";
import kawaiiArtData from "src/configs/kwaii";
import { BASE18, RENT_CONTRACT_TIME_BASE } from "src/configs/constance";
import ABI from "src/contract-logic/abis/NFTRentABI.json";
import { handleEmit } from "src/contract-logic/logic";
import { getCurrentContractAddresses } from "src/configs/address";
import { getWeb3Sender, web3Reader } from "src/wallet-connection";
import multiCall from "../../utils/multicall";

const { RENT_CONTRACT_ADDRESS } = getCurrentContractAddresses();
var contractReader = null;
var contractSender = null;

function getContractReader() {
  if (!contractReader) {
    contractReader = new web3Reader.eth.Contract(ABI, RENT_CONTRACT_ADDRESS);
  }
  if (!contractReader) {
    throw new Error("Failed to create rent contract reader");
  }
  return contractReader;
}

function getContractSender() {
  const web3Sender = getWeb3Sender();
  if (!contractSender) {
    contractSender = new web3Sender.eth.Contract(ABI, RENT_CONTRACT_ADDRESS);
  }
  if (!contractSender) {
    throw new Error("Failed to create rent contract sender");
  }
  return contractSender;
}

async function createNewRentalNFT(
  dispatch,
  tokenId,
  tokenAmount,
  price,
  minTime,
  maxTime,
  isBNB,
  accountAddress,
  data = "0x00"
) {
  const emit = getContractSender()
    .methods.createNewNftRent(tokenId, tokenAmount, price, minTime, maxTime, isBNB, data)
    .send({ from: accountAddress });
  handleEmit(dispatch, emit, "Lease NFT");
  return await emit;
}

async function getOwnerSize() {
  const res = await getContractReader().methods.ownerSize().call();
  return parseInt(res);
}

async function getOwnerAddressAtIndex(index) {
  const res = await getContractReader().methods.ownerAddressAtIndex(index).call();
  return res;
}

async function getTokenListSizeOfOwner(ownerAddress) {
  const res = await getContractReader().methods.tokenListSize(ownerAddress).call();
  return res;
}

async function getTokenIdAtIndex(ownerAddress, index) {
  const res = await getContractReader().methods.tokenIdAtIndex(ownerAddress, index).call();
  return res;
}

async function getNFTRentListing(tokenId, nftOwnerAddress) {
  const res = await getContractReader().methods.checkRentListing(tokenId, nftOwnerAddress).call();
  return res;
}

async function getTokenMetadata(tokenId, ownerAddress) {
  const nftRentListing = await getNFTRentListing(tokenId, ownerAddress);
  const available = await getAvailableStock(ownerAddress, tokenId);
  const quantity =
    parseInt(nftRentListing.unallocatedTokens) +
    (await getTotalRentingNFT(nftRentListing.orderHistory, nftRentListing.allocatedTokens));
  const res = {
    isBnB: nftRentListing.isBnB,
    isAvailableForRent: nftRentListing.isAvailableForRent,
    minTime: parseInt(parseInt(nftRentListing.minTime) / RENT_CONTRACT_TIME_BASE),
    maxTime: parseInt(parseInt(nftRentListing.maxTime) / RENT_CONTRACT_TIME_BASE),
    currentOwner: ownerAddress,
    price: parseFloat(BigNumber(nftRentListing.price).dividedBy(BASE18).toFixed()),
    ...kawaiiArtData[tokenId],
    id: parseInt(tokenId),
    available,
    quantity,
  };
  return res;
}

async function getAllNFTRentListingOfOwner(ownerAddress) {
  const tokenListSize = await getTokenListSizeOfOwner(ownerAddress);
  let res = [];
  for (let index = 0; index < tokenListSize; ++index) {
    const tokenId = await getTokenIdAtIndex(ownerAddress, index);
    const nftRentListing = await getNFTRentListing(tokenId, ownerAddress);
    const available = await getAvailableStock(ownerAddress, tokenId);
    const quantity =
      parseInt(nftRentListing.unallocatedTokens) +
      (await getTotalRentingNFT(nftRentListing.orderHistory, nftRentListing.allocatedTokens));
    res.push({
      isBnB: nftRentListing.isBnB,
      isAvailableForRent: nftRentListing.isAvailableForRent,
      minTime: parseInt(parseInt(nftRentListing.minTime) / RENT_CONTRACT_TIME_BASE),
      maxTime: parseInt(parseInt(nftRentListing.maxTime) / RENT_CONTRACT_TIME_BASE),
      currentOwner: ownerAddress,
      price: parseFloat(BigNumber(nftRentListing.price).dividedBy(BASE18).toFixed()),
      ...kawaiiArtData[tokenId],
      id: parseInt(tokenId),
      available,
      quantity,
    });
  }
  return res;
}

async function getTotalRentingNFT(orderHistories, allocatedTokens) {
  const expiredStatusArr = await multiCall(
    ABI,
    orderHistories.map((orderId) => ({
      address: RENT_CONTRACT_ADDRESS,
      name: "isOrderExpired",
      params: [orderId],
    }))
  );
  const total = expiredStatusArr
    .flat()
    .reduce((prev, item, idx) => (item ? prev : prev + parseInt(allocatedTokens[idx])), 0);
  return total;
}

async function getAllNFTRentListing() {
  const ownerSize = await getOwnerSize();
  let ownerAddresses = [];
  for (let index = 0; index < ownerSize; ++index) {
    const addr = await getOwnerAddressAtIndex(index);
    ownerAddresses.push(addr);
  }
  let res = [];
  for (let ownerAddress of ownerAddresses) {
    const ownerNFTRentListing = await getAllNFTRentListingOfOwner(ownerAddress);
    res = res.concat(ownerNFTRentListing);
  }
  return res;
}

async function getAvailableStock(holderAddress, tokenId) {
  const res = await getContractReader().methods.getAvailableStock(holderAddress, tokenId).call();
  return parseInt(res);
}

async function makeRentOrder(dispatch, nftOwner, tokenId, tokenAmount, duration, accountAddress, data = "0x00") {
  const emit = getContractSender()
    .methods.makeRentOrder(nftOwner, tokenId, tokenAmount, duration, data)
    .send({ from: accountAddress });
  handleEmit(dispatch, emit, "Place Rent Order");
  const res = await emit;
  return res;
}

async function makeRentOrderBNB(nftOwner, tokenId, tokenAmount, duration, payAmount, accountAddress, data = "0x00") {
  const res = await getContractSender()
    .methods.makeRentOrder(nftOwner, tokenId, tokenAmount, duration, data)
    .send({ from: accountAddress, value: payAmount });
  return res;
}

async function getRenterInfoPublic(ownerAddress) {
  return await getContractReader().methods.getRenterInfoPublic(ownerAddress).call();
}

async function getRentingListOfOwner(ownerAddress) {
  const res = await getContractReader().methods.getRenterInfoPublic(ownerAddress).call();
  const tokenOwnersOfRenterHistory = res.tokenOwnersOfRenterHistory;
  const tokenIdsOfRenterHistory = res.tokenIdsOfRenterHistory;
  const orderIdsOfRenterHistory = res.orderIdsOfRenterHistory;
  let nftRentingList = [];
  for (let i = 0; i < tokenOwnersOfRenterHistory.length; ++i) {
    let _tokenId = tokenIdsOfRenterHistory[i];
    let _nftOwner = tokenOwnersOfRenterHistory[i];
    let _orderId = orderIdsOfRenterHistory[i];
    let _nftRenter = ownerAddress;
    const isOrderExpired = await getContractReader().methods.isOrderExpired(_orderId).call();
    if (!isOrderExpired) {
      const isValidOrder = await getContractReader().methods.verifyRent(_tokenId, _nftOwner, _nftRenter).call();
      if (isValidOrder) {
        const rentingNFT = await getContractReader().methods.checkRentActive(_tokenId, _nftOwner, _nftRenter).call();
        nftRentingList.push({
          ...kawaiiArtData[_tokenId],
          id: parseInt(_tokenId),
          quantity: parseInt(rentingNFT.amount),
          duration: parseInt(rentingNFT.duration) / RENT_CONTRACT_TIME_BASE,
          orderId: parseInt(rentingNFT.orderId),
          startTime: parseInt(rentingNFT.startTime) * 1000,
        });
      }
    }
  }
  return nftRentingList;
}

async function stopRenting(dispatch, tokenId, accountAddress) {
  const emit = getContractSender().methods.disableRent(tokenId).send({ from: accountAddress });
  handleEmit(dispatch, emit, "Stop Leasing");
  return await emit;
}

async function enableRent(dispatch, tokenId, accountAddress) {
  const emit = getContractSender().methods.enableRent(tokenId).send({ from: accountAddress });
  handleEmit(dispatch, emit, "Enable Lease");
  return await emit;
}

async function withdrawNFT(dispatch, tokenId, tokenAmount, accountAddress, data = "0x00") {
  const emit = getContractSender().methods.withdrawNft(tokenId, tokenAmount, data).send({ from: accountAddress });
  handleEmit(dispatch, emit, "Withdraw NFTs");
  return await emit;
}

async function updateMinAndMaxTime(dispatch, tokenId, minTime, maxTime, accountAddress) {
  const emit = getContractSender()
    .methods.updateMinAndMaxTime(tokenId, minTime, maxTime)
    .send({ from: accountAddress });
  handleEmit(dispatch, emit, "Update Time");
  await emit;
}

async function updatePrice(dispatch, tokenId, price, accountAddress) {
  const emit = getContractSender().methods.updatePrice(tokenId, price).send({ from: accountAddress });
  handleEmit(dispatch, emit, "Update Price");
  await emit;
}

export default {
  ABI,
  ADDRESS: RENT_CONTRACT_ADDRESS,
  createNewRentalNFT,
  getOwnerSize,
  getOwnerAddressAtIndex,
  getTokenListSizeOfOwner,
  getTokenIdAtIndex,
  getTokenMetadata,
  getNFTRentListing,
  getAllNFTRentListingOfOwner,
  getAllNFTRentListing,
  getAvailableStock,
  makeRentOrder,
  makeRentOrderBNB,
  getRenterInfoPublic,
  getRentingListOfOwner,
  stopRenting,
  enableRent,
  withdrawNFT,
  getTotalRentingNFT,
  updateMinAndMaxTime,
  updatePrice,
};
